import { autoinject, computedFrom, transient } from "aurelia-framework";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import {
    AddSubtaskRequest, EmailRecipientTypeEnum,
    RfiTemplateCategoryEnum, VettingActionEnum
} from "../../../../../../models/vetting/vetting.models";
import * as Enumerable from "linq";
import {
    ValidationActivation,
    ValidationTypeEnum,
    RulesHolder
} from "../../../../../../client-validation-system/validations.models";
import {
    ClientSideValidator,
    EntityValidationsService
} from "../../../../../../client-validation-system/validations-service";
import { MultiSelectLookupControl } from "../../../../../components/lookups/multi-select-lookup-control/multi-select-lookup-control";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BusinessUnitVetAlertRecipientModel } from "../../../../../../models/customers/customer.models";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { SubtasksService } from "../../../../../../services/subtasks/subtasks.service";
import { RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { DataSourcesVm } from "../../../../../../models/pscs/psc.models";
import { AttachmentFile } from "../../../../../../models/uploader/attachmentFile";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CompRichTextEditor } from "@rightship-tech/common-ui/components/comp-rich-text-editor/comp-rich-text-editor";

@autoinject
@transient()
export class AddSubtaskDialog {
    accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";

    _fallbackEmails: { email: string, emailRecipientTypeId: number, isShipManagerEmail: boolean }[] = [{
        email: "",
        emailRecipientTypeId: null,
        isShipManagerEmail: false
    }];
    _changeStatusToAwaitingResponse: boolean = false;

    isEmailsFixed: boolean;
    isWet: boolean;
    selectedRfiTypes: MultiSelectLookupControl;
    subtaskCorrespondenceId: number;
    alertEmails: BusinessUnitVetAlertRecipientModel[];
    entityId: number;
    entityTypeId: number;
    inspectionTypeId: number;
    isPartialRFI: boolean;
    subtaskId: number;
    isNew: boolean = true;
    reload: (() => void);
    requestedBy: string;
    message: string;
    comment: string;
    resendEmail: boolean = true;
    statusSnippet: string = "";
    subtaskTypes: number[] = [];
    emails: { email: string, emailRecipientTypeId: number, isShipManagerEmail: boolean, recipientType? : string }[] = [{
        email: "",
        emailRecipientTypeId: null,
        isShipManagerEmail: false
    }];
    linkedEntityId: number;
    allLinkedEntityIds: number[];
    linkedEntityTypeId: number;
    canShowAwaitingResponseCheckBox: boolean;
    subtaskName: string = null;
    loadMessageFromSubtaskTypes: boolean = false;
    skipInvite: boolean = false;
    canShowSaveAndFinaliseButton: boolean;
    dataSources: DataSourcesVm[] = [];
    sendMethods: any[] = [{ id: 1, name: "Send by Api", sendViaApi: true }, { id: 2, name: "Send by Email", sendViaApi: false }];
    selectedMethod: any;
    selectedMethodId: number;
    dataSourceId: number;
    selectedDataSource: DataSourcesVm;

    shipManagerEmails : string[] = []

    constructor(public dialogClientService: DialogClientService, private notificationClientService: NotificationClientService, private vetClientService: VettingService,
        private cacheClient: CacheClientService, private entityValidationsService: EntityValidationsService,
        private subtaskService: SubtasksService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private userClaimsService: UserClaimsService) {
        this.canShowAwaitingResponseCheckBox = this.userClaimsService.hasPermission(Claims.ShowChangestatustoAwaitingcheckbox);
        this.canShowSaveAndFinaliseButton = this.userClaimsService.hasPermission(Claims.ShowSaveandFinaliseButton);
    }

    public onUserSelected(email: { email: string }, user) {
        email.email = user ? user.email : null;
    }

    @computedFrom("dataSources", "dataSources.length", "isInternal")
    get showSendMethod(): boolean {
        const showSendMethod = this.dataSources && this.dataSources.length > 0 && !this.isInternal;
        if (showSendMethod && this.selectedMethod.sendViaApi) {
            this._changeStatusToAwaitingResponse = true;
        }

        return showSendMethod;
    }

    @computedFrom("_changeStatusToAwaitingResponse")
    get changeStatusToAwaitingResponse() {
        return this._changeStatusToAwaitingResponse;
    }

    set changeStatusToAwaitingResponse(x: boolean) {
        this._changeStatusToAwaitingResponse = x;
        this.setRFIRules();
    }

    @computedFrom('entityTypeId')
    get entityName() {
        if (this.entityTypeId == EntityTypes.RightShipInspection) {
            if (this.inspectionTypeId == RightShipInspectionType.TankerInspection)
                return "Tanker Inspection";
            if (this.inspectionTypeId == RightShipInspectionType.CargillSurvey)
                return "Cargill Inspection";
            return "RightShip Inspection";
        }

        if (this.entityTypeId == EntityTypes.FBR) {
            return "FBR";
        }

        if (this.entityTypeId == EntityTypes.GHGVerification) {
            return "Environmental Verification";
        }

        if (this.entityTypeId == EntityTypes.Cfr) {
            return "CFR";
        }

        if (this.entityTypeId == EntityTypes.PSC) {
            return "PSC";
        }

        if (this.entityTypeId == EntityTypes.Incident) {
            return "Incident";
        }

        if (this.entityTypeId == EntityTypes.Vet) {
            return "Vet";
        }
        if (this.entityTypeId == EntityTypes.VoyageDataSubmission) {
            return "Voyage Data Submission";
        }

        return "";
    }

    @computedFrom('entityTypeId')
    get isVesselApplicationForm(): boolean {
        if (this.entityTypeId == EntityTypes.VesselApplicationForms)
            return true;
        return false;
    }

    @computedFrom('entityTypeId')
    get canShowAwaitingResponseCheckboxIfVet(): boolean {
        return !(!this.canShowAwaitingResponseCheckBox && this.entityTypeId == EntityTypes.Vet);
    }

    getSelectedMethod(method: any) {
        this.selectedMethod = method.detail.item;
        if (!this.selectedMethod.sendViaApi && this._changeStatusToAwaitingResponse) {
            this._changeStatusToAwaitingResponse = false;
        }
        this.setRFIRules();
    }

    getSelectedDataSource(dataSource: any) {
        this.selectedDataSource = dataSource.detail.item as DataSourcesVm;
    }
    getParametersForTypes() {
        if (this.entityTypeId == EntityTypes.RightShipInspection) {
            if (this.inspectionTypeId == RightShipInspectionType.TankerInspection)
                return RfiTemplateCategoryEnum.TankerInspection;
            return RfiTemplateCategoryEnum.RightShipInspection;
        }

        if (this.entityTypeId == EntityTypes.FBR) {
            return RfiTemplateCategoryEnum.FBR;
        }

        if (this.entityTypeId == EntityTypes.CrewWelfare) {
            return RfiTemplateCategoryEnum.CrewWelfare;
        }

        if (this.entityTypeId == EntityTypes.GHGVerification) {
            return RfiTemplateCategoryEnum.GHG;
        }

        if (this.entityTypeId == EntityTypes.Cfr) {
            return RfiTemplateCategoryEnum.CFR;
        }

        if (this.entityTypeId == EntityTypes.PSC) {
            return RfiTemplateCategoryEnum.PSC;
        }

        if (this.entityTypeId == EntityTypes.Incident) {
            return RfiTemplateCategoryEnum.Incident;
        }

        if (this.entityTypeId == EntityTypes.Vet) {
            return RfiTemplateCategoryEnum.VET;
        }
        if (this.entityTypeId == EntityTypes.VoyageDataSubmission) {
            return RfiTemplateCategoryEnum.VoyageDataSubmission;
        }

        if (this.entityTypeId == EntityTypes.SafetyRatingReview) {
            return RfiTemplateCategoryEnum.SafetyRatingReview;
        }

        return "";
    }

    createSubtask: boolean;
    liaseWithShipManagers: boolean;
    isInternal: boolean;

    async initialize(model) {
        this.isInternal = model.isInternal;
        this.liaseWithShipManagers = model.liaseWithShipManagers;
        this.createSubtask = model.createSubtask;
        this.subtaskId = model.subtaskId;
        this.entityId = model.entityId;
        this.linkedEntityId = model.linkedEntityId;
        this.linkedEntityTypeId = model.linkedEntityTypeId;
        this.entityTypeId = model.entityTypeId;
        this.inspectionTypeId = model.inspectionTypeId;
        this.requestedBy = model.requestedBy;
        this.isWet = model.isWet;
        this.subtaskName = model.subtaskName;
        this.skipInvite = model.skipInvitation;
        this.changeStatusToAwaitingResponse = model.changeStatusToAwaitingResponse;
        this.reload = model.reload;
        this.dataSources = model?.dataSources ? (model.dataSources as DataSourcesVm[]) : [];
        let emails = model.emails ? CloneHelper.cloneObject(model.emails) : [{ email: "" }];
        emails = emails.concat(CloneHelper.cloneObject(this._fallbackEmails.filter(x => x.email != null && x.email != ""))).filter(x => x.email);

        if (emails.length > 0 && !this.isInternal) {
            this.emails = emails.map(x => {
                return { email: x.email, emailRecipientTypeId: x.emailRecipientTypeId, isShipManagerEmail: x.isShipManagerEmail, isFromConfig: x.isFromConfig, recipientType: x.recipientType }
            });
            this.removeDuplicateEmails();
        }
        else {
            this.emails = [{ email: "", emailRecipientTypeId: EmailRecipientTypeEnum.To, isShipManagerEmail: false }];
            this.removeDuplicateEmails();
        }

        this.isEmailsFixed = model.isEmailsFixed;
        this.isNew = true;
        this.allLinkedEntityIds = model.linkedEntityId ? [model.linkedEntityId] : [];
        this.message = null;
        this.statusSnippet = "";
        this.comment = "";
        this.subtaskTypes = [];
        this.messageLastSelectedItemIds = [];
        this.statusSnippetLastSelectedItemIds = [];
        this.isPartialRFI = false;

        if (this.dataSources.length > 0) {
            if (this.dataSources.length == 1) {
                this.selectedDataSource = this.dataSources[0];
            }
            this.selectedMethod = this.sendMethods[0];
        }

        if (model.isPartialRFI) {
            const isVet = this.entityTypeId == EntityTypes.Vet;
            const id = this.linkedEntityId ? this.linkedEntityId : null;
            const rfiKey = this.getRfiKey();
            if (model.createSubtask) {
                const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(rfiKey, CacheLocation.SessionStorage, false);
                if (rfis) {
                    if (isVet) {
                        this.allLinkedEntityIds = Enumerable.from(rfis)
                            .where(x => x.value != null)
                            .select(x => Number(x.key))
                            .toArray();
                        this.linkedEntityTypeId = model.linkedEntityTypeId? model.linkedEntityTypeId: 
                            Enumerable.from(rfis).select(x => x?.value?.linkedEntityTypeId).where(x => x != null).firstOrDefault();
                    }
                    const message = Enumerable.from(rfis).where(x => x.value != null).select(x => x.value.message).toJoinedString("<br/>");
                    const subtaskTypes = Enumerable.from(rfis).where(x => x.value != null).selectMany(x => x.value.subtaskTypes).distinct().toArray();
                    this.message = message;
                    this.messageLastSelectedItemIds = <number[]>subtaskTypes;
                    this.subtaskTypes = <number[]>subtaskTypes;
                }
            } else {
                this.isPartialRFI = true;
                const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(rfiKey, CacheLocation.SessionStorage, false);
                if (rfis) {
                    const rfi: AddSubtaskRequest = rfis[id];
                    if (rfi) {
                        this.message = rfi.message;
                        this.subtaskTypes = rfi.subtaskTypes;
                        this.messageLastSelectedItemIds = <number[]>rfi.subtaskTypes;
                        this.statusSnippetLastSelectedItemIds = <number[]>rfi.subtaskTypes;
                    }
                } else if (model.subtaskTypes) {
                    this.subtaskTypes = model.subtaskTypes;
                }
            }

            await this.checkForVettingEmails();
            await this.loadPartialRFIs();
        } else if (this.subtaskId) {
            this.isNew = false;
            const subtask = await this.subtaskService.getSubtask(this.subtaskId);
            this.changeStatusToAwaitingResponse = subtask.changeStatusToAwaitingResponse;
            this.message = subtask.message;
            this.statusSnippet = subtask.statusSnippet;
            this.comment = subtask.comment;
            this.subtaskTypes = subtask.subtaskTypeIds;
            this.emails = subtask.subtaskCorrespondenceEmails.map(x => {
                return {
                    email: x.email,
                    emailRecipientTypeId: x.emailRecipientTypeId,
                    isShipManagerEmail: x.isShipManagerEmail,
                    isFromConfig: x.isFromConfig
                }
            });
            this.removeDuplicateEmails();
        } else {
            if (!model.emails || model.emails.length == 0) {
                await this.checkForVettingEmails();
            }

            if (model.subtaskTypes) {
                this.subtaskTypes = model.subtaskTypes;
                this.loadMessageFromSubtaskTypes = true;
            }
        }

        if (this.isInternal)
            this.userEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["UserSearch?internal=1"])
        else
            this.userEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["UserSearch"]);

        if(this.liaseWithShipManagers) {
            this.shipManagerEmails = this.emails.filter(x => x.isShipManagerEmail)?.map(x => x.email.trim().toLowerCase());
        }

    }

    activate(model) {
        return this.initialize(model);
    }

    countRFIs: number = 1;
    async loadPartialRFIs() {
        if (this.entityTypeId != EntityTypes.Vet) return;
        const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-${this.entityId}`, CacheLocation.SessionStorage, false);
        this.countRFIs = rfis ? Enumerable.from(rfis).where(x => x.value != null).count() + 1 : 1;
    }

    get anyShipManager() {
        return this.emails.filter(x => x.isShipManagerEmail).length > 0;
    }

    async checkForVettingEmails() {
        if (this.entityTypeId == EntityTypes.Vet && !this.emails && !this.isInternal) {
            const alertEmails = await this.vetClientService.getBusinessUnitVetAlertRecipients(this.entityId, VettingActionEnum.VetRequiresFurtherInformation);

            if (alertEmails.length > 0) {
                this.alertEmails = alertEmails;
                this.emails = alertEmails.filter(x => x.email).map(x => {
                    return { email: x.email, emailRecipientTypeId: x.emailRecipientTypeId, isShipManagerEmail: false }
                });
                this.removeDuplicateEmails();
            }
        }
    }

    validatorEmails: ClientSideValidator;
    validator: ClientSideValidator;

    valuesLoaded() {
        if (this.createSubtask) {
            if (!this.message)
                this.selectedItemsChangedMessage();
            if (!this.statusSnippet)
                this.selectedItemsChangedStatusSnippet();
        }

        if (this.loadMessageFromSubtaskTypes) {
            this.selectedItemsChangedMessage();
            this.loadMessageFromSubtaskTypes = false;
        }
    }

    attached() {

        const rulesHolderEmails: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "RFI Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };

        if (!this.isVesselApplicationForm)
            this.setRFIRules();

        this.validatorEmails = this.entityValidationsService.getValidator(this.emails, rulesHolderEmails);
    }

    private setRFIRules() {
        if (this.validator && this.validator.dispose)
            this.validator.dispose();

        const rulesHolder: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "RFI",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Status snippet max length is 200 characters",
                parameters: '{ "length": 200 }', property: "statusSnippet",
                validationType: ValidationTypeEnum.MaxLength
            }]
        };

        if (this._changeStatusToAwaitingResponse == true) {
            rulesHolder.rules.push({
                errorMessage: "Status snippet is required",
                parameters: null, property: "statusSnippet",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Comments are required",
                parameters: null, property: "comment",
                validationType: ValidationTypeEnum.Required
            });
        }


        if (this.selectedMethod && this.selectedMethod.sendViaApi) {
            rulesHolder.rules.push({
                errorMessage: "Data Source is required",
                parameters: null,
                property: "selectedDataSource",
                validationType: ValidationTypeEnum.Required,

            });
        }
        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    cancel() {
        this.dialogClientService.hide();
    }

    removeEmail(i) {
        this.emails.splice(i, 1);
    }

    addEmail() {
        this.emails.push({ email: "", emailRecipientTypeId: null, isShipManagerEmail: false });
    }

    public attachments: File[] = [];
    public files: AttachmentFile[] = [];

    public checkShipManagerEmails() {
        if(this.liaseWithShipManagers) {
            for(const email of this.emails) {
            
                if(this.shipManagerEmails.includes(email.email.trim().toLowerCase())) {
                    email.isShipManagerEmail = true;
                }
            }
        }
    }

    async submit(isContacVetRequest: boolean = false) {
        try {
            const validator1 = await this.validatorEmails.validate();
            const validator2 = this.validator ? (await this.validator.validate()) : null;
            if (!validator1.valid || validator2 && !validator2.valid)
                return;

            this.checkShipManagerEmails()
            await this.addSubtask(true);

            if (!isContacVetRequest) {
                const rfiKey = this.getRfiKey();
                await this.cacheClient.remove(rfiKey, CacheLocation.SessionStorage);
            }

            if (this.reload)
                this.reload();
            this.notificationClientService.show(`${this.subtaskId ? 'RFI Saved' : 'RFI Sent'}`, `RFI ${this.subtaskId ? 'saved' : 'sent'} successfully.`, NotificationType.Success);
            this.dialogClientService.hide();
        } catch (e) {
            if (e?.content?.exception != 'GenesisException') {
                this.notificationClientService.show(`${this.subtaskId ? 'Saving of RFI failed' : 'Sending of RFI failed'}`, `Failed to ${this.subtaskId ? 'save' : 'send'} RFI`, NotificationType.Error);
            }
        }
    }

    skipInvitation() {
        return this.skipInvite ? this.skipInvite : false;
    }


    public userEntityTypes: EntityTypeConfigurationModel[] = null;

    private async addSubtask(submit: boolean) {
        const request: AddSubtaskRequest = {
            entityId: this.entityId,
            entityTypeId: this.entityTypeId,
            isInternal: this.isInternal,
            subtaskId: this.subtaskId,
            skipInvitation: this.skipInvitation(),
            changeStatusToAwaitingResponse: this.changeStatusToAwaitingResponse,
            emails: this.emails,
            message: this.message,
            resendEmail: this.resendEmail,
            statusSnippet: this.statusSnippet,
            comment: this.comment,
            submit: submit,
            subtaskTypes: this.subtaskTypes,
            subtaskCorrespondenceId: this.subtaskCorrespondenceId,
            linkedEntityIds: this.allLinkedEntityIds || [],
            linkedEntityTypeId: this.linkedEntityTypeId,
            attachments: submit ? this.attachments : [],
            files: submit ? this.files : [],
            name: this.subtaskName,
            dataSourceId: this.selectedMethod && this.selectedMethod.sendViaApi ? this.selectedDataSource.dataSourceId : 0
        };
        if (this.entityId && this.entityTypeId)
            this.subtaskCorrespondenceId = await this.subtaskService.addSubtask(request);
    }

    async saveAndFinalise() {
        await this.save(false);
        await this.initialize({
            entityId: this.entityId,
            entityTypeId: this.entityTypeId,
            requestedBy: this.requestedBy,
            isInternal: this.isInternal,
            isWet: this.isWet,
            emails: this.emails,
            createSubtask: true,
            liaseWithShipManagers: this.liaseWithShipManagers,
            isPartialRFI: true,
            reload: this.reload
        });
    }

    async save(closeDialog: boolean = true) {
        try {
            this.checkShipManagerEmails()
            const request: AddSubtaskRequest = {
                entityId: this.entityId,
                entityTypeId: this.entityTypeId,
                isInternal: this.isInternal,
                subtaskId: this.subtaskId,
                skipInvitation: this.skipInvitation(),
                changeStatusToAwaitingResponse: this.changeStatusToAwaitingResponse,
                emails: this.emails,
                message: this.message,
                submit: true,
                files: this.files,
                resendEmail: this.resendEmail,
                statusSnippet: this.statusSnippet,
                comment: this.comment,
                subtaskTypes: this.subtaskTypes,
                linkedEntityIds: this.allLinkedEntityIds || [],
                linkedEntityTypeId: this.linkedEntityTypeId,
                attachments: this.attachments,
                dataSourceId: this.selectedMethod && this.selectedMethod.sendViaApi ? this.selectedDataSource.dataSourceId : 0
            };


            const rfiKey = this.getRfiKey();
            let rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(rfiKey, CacheLocation.SessionStorage, false);

            if (!rfis) rfis = {};
            const id = this.linkedEntityId ? this.linkedEntityId : null;
            rfis[id] = request;
            await this.cacheClient.put<{ [index: number]: AddSubtaskRequest }>(rfiKey, rfis, CacheLocation.SessionStorage);

            if (this.reload)
                this.reload();
            this.notificationClientService.show(`${this.subtaskId || this.isPartialRFI ? 'Save' : 'Create'} RFI`, `RFI ${this.subtaskId || this.isPartialRFI ? 'saved' : 'created'} successfully.`, NotificationType.Success);
            if (closeDialog)
                this.dialogClientService.hide();
        } catch {
            this.notificationClientService.show(`${this.subtaskId || this.isPartialRFI ? 'Save' : 'Create'} RFI`, `Failed to ${this.subtaskId || this.isPartialRFI ? 'save' : 'create'} RFI`, NotificationType.Error);
        }
    }

    messageLastSelectedItemIds: number[] = [];
    statusSnippetLastSelectedItemIds: number[] = [];

    selectedItemsChangedMessage() {
        if (!this.selectedRfiTypes || !this.selectedRfiTypes.itemSource) return;
        const notSelected = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) == -1);
        if (this.message) {
            notSelected.forEach(x => {
                if (!x.additionalData && x.additionalData.template) return;

                const index = this.messageLastSelectedItemIds.indexOf(x.id);
                if (index >= 0) {
                    this.messageLastSelectedItemIds.splice(index, 1);

                    const textToSearch = CompRichTextEditor.getOptimizedHtml(x.additionalData.template);
                    this.message = this.message.split(textToSearch).join("");
                }
            });
        }

        const newItems = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) != -1 && this.messageLastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.message && !this.message.endsWith("<br></p>"))
                this.message += "<br/>";
            else if (!this.message) {
                this.message = "";
            }

            if (x && x.additionalData && x.additionalData.template)
                this.message += x.additionalData.template;
            this.messageLastSelectedItemIds.push(x.id);
        });
    }

    selectedItemsChangedStatusSnippet() {
        if (!this.selectedRfiTypes || !this.selectedRfiTypes.itemSource) return;
        const notSelected = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) == -1);
        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.statusSnippetLastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.statusSnippetLastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) != -1 && this.statusSnippetLastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.name)
                this.statusSnippet += x.name;

            this.statusSnippetLastSelectedItemIds.push(x.id);
        });
    }

    selectedItemsChanged() {
        this.selectedItemsChangedMessage();
        this.selectedItemsChangedStatusSnippet();
    }

    removeDuplicateEmails() {
        this.emails = Enumerable.from(this.emails).distinct(x => { return { email: x.email, emailRecipientTypeId: x.emailRecipientTypeId }; }).toArray()
    }

    private getRfiKey() {
        return this.entityTypeId == EntityTypes.VoyageDataSubmission
            ? `RFI-VDS-${this.entityId}`
            : this.entityTypeId == EntityTypes.Vet
                ? `RFI-${this.entityId}`
                : this.entityTypeId == EntityTypes.FBR
                    ? `RFI-FBR-${this.entityId}`
                    : this.entityTypeId == EntityTypes.SafetyRatingReview
                        ? `RFI-SRR-${this.entityId}`
                        : this.entityTypeId == EntityTypes.CrewWelfare
                            ? `RFI-CW-${this.entityId}`
                            : null;
    }
}
